import { CONDITION_OPERATORS, compileCondition, checkExpressionSyntax, createSchemaScope, humaniseLabel, type Condition, type ConditionGroup, type ConditionRule, type DataSource, type FieldSchema } from "@broadpaper/core";
import { Button, Segmented, Select, TextInput } from "../../ui/primitives.js";
import { BindingField } from "./BindingField.js";
import { useState } from "react";

interface Props {
  value: Condition | undefined;
  onChange: (value: Condition | undefined) => void;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  allowExpression?: boolean;
}

function isGroup(v: Condition | undefined): v is ConditionGroup {
  return !!v && typeof v === "object" && "op" in v;
}

/** Visual AND/OR rule builder that compiles to the expression language. */
export function ConditionBuilder({ value, onChange, dataSources, scopeVars, allowExpression = true }: Props) {
  const [advanced, setAdvanced] = useState(typeof value === "string" && value.trim() !== "");
  const group: ConditionGroup = isGroup(value) ? value : { op: "and", rules: [] };
  const compiled = compileCondition(value);

  if (advanced) {
    const src = typeof value === "string" ? value : compiled ?? "";
    const err = src.trim() ? checkExpressionSyntax(src) : null;
    return (
      <div className="bpe-cond">
        <TextInput value={src} onChange={(v) => onChange(v.trim() ? v : undefined)} mono lazy placeholder="e.g. client.riskScore > 7 and client.isRetired" invalid={!!err} />
        {err ? <div className="bpe-field__error">{err.message}</div> : null}
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" onClick={() => { setAdvanced(false); onChange({ op: "and", rules: [] }); }}>Use rule builder</Button>
          {value ? <Button size="sm" variant="ghost" icon="x" onClick={() => onChange(undefined)}>Clear</Button> : null}
        </div>
      </div>
    );
  }

  const update = (g: ConditionGroup) => onChange(g.rules.length ? g : undefined);
  return (
    <div className="bpe-cond">
      <GroupEditor group={group} onChange={update} dataSources={dataSources} scopeVars={scopeVars} depth={0} />
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <Button size="sm" icon="plus" onClick={() => update({ ...group, rules: [...group.rules, { left: "", operator: "eq", right: "" }] })}>Rule</Button>
        <Button size="sm" icon="plus" onClick={() => update({ ...group, rules: [...group.rules, { op: "or", rules: [{ left: "", operator: "eq", right: "" }] }] })}>Group</Button>
        {allowExpression ? <Button size="sm" variant="ghost" icon="braces" onClick={() => setAdvanced(true)}>Expression</Button> : null}
      </div>
      {/*
        What the rules come to.
        
        The compiled source where there is an expression editor to read it with,
        and the same thing in field names where there is not — a Cloud user was
        shown `isNotEmpty(invoice.taxRate)` under a rule they had built out of
        dropdowns, which is the syntax the builder exists to spare them.
      */}
      {compiled ? (
        allowExpression ? (
          <div className="bpe-field__hint bpe-mono" title={compiled}>{compiled}</div>
        ) : (
          <div className="bpe-field__hint">{humaniseLabel(compiled, createSchemaScope(dataSources).child(scopeVars ?? {}))}</div>
        )
      ) : (
        <div className="bpe-field__hint">Always shown</div>
      )}
    </div>
  );
}

function GroupEditor({ group, onChange, dataSources, scopeVars, depth }: { group: ConditionGroup; onChange: (g: ConditionGroup) => void; dataSources: DataSource[]; scopeVars?: Record<string, FieldSchema>; depth: number }) {
  const setRule = (i: number, r: ConditionRule | ConditionGroup) => onChange({ ...group, rules: group.rules.map((x, j) => (j === i ? r : x)) });
  const remove = (i: number) => onChange({ ...group, rules: group.rules.filter((_, j) => j !== i) });
  return (
    <div className={depth > 0 ? "bpe-cond__group" : undefined}>
      {group.rules.length > 1 ? (
        <div className="bpe-cond__op" style={{ marginBottom: 6 }}>
          <span className="bpe-muted" style={{ fontSize: "var(--bpe-text-xs)" }}>Match</span>
          <Segmented value={group.op} onChange={(op) => onChange({ ...group, op: op as "and" | "or" })} options={[{ value: "and", label: "All (AND)" }, { value: "or", label: "Any (OR)" }]} className="bpe-mode" />
        </div>
      ) : null}
      {group.rules.map((r, i) => (
        <div key={i} className="bpe-cond__rule" style={{ marginBottom: 6 }}>
          {"op" in r ? (
            <GroupEditor group={r} onChange={(g) => setRule(i, g)} dataSources={dataSources} scopeVars={scopeVars} depth={depth + 1} />
          ) : (
            <RuleEditor rule={r} onChange={(x) => setRule(i, x)} dataSources={dataSources} scopeVars={scopeVars} />
          )}
          <Button size="sm" variant="ghost" icon="x" tooltip="Remove" onClick={() => remove(i)} />
        </div>
      ))}
      {depth > 0 ? (
        <Button size="sm" icon="plus" onClick={() => onChange({ ...group, rules: [...group.rules, { left: "", operator: "eq", right: "" }] })}>Rule</Button>
      ) : null}
    </div>
  );
}

function RuleEditor({ rule, onChange, dataSources, scopeVars }: { rule: ConditionRule; onChange: (r: ConditionRule) => void; dataSources: DataSource[]; scopeVars?: Record<string, FieldSchema> }) {
  const op = CONDITION_OPERATORS.find((o) => o.value === rule.operator) ?? CONDITION_OPERATORS[0]!;
  const rightStr = rule.right === undefined || rule.right === null ? "" : String(rule.right);
  return (
    <div className="bpe-cond__row">
      <BindingField value={rule.left} onChange={(left) => onChange({ ...rule, left })} dataSources={dataSources} scopeVars={scopeVars} placeholder="Choose a field" />
      <Select value={rule.operator} onChange={(v) => onChange({ ...rule, operator: v as ConditionRule["operator"] })} options={CONDITION_OPERATORS.map((o) => ({ value: o.value, label: o.label }))} />
      {op.needsValue ? (
        <TextInput
          value={rightStr}
          lazy
          placeholder="Value, or {{ field }}"
          onChange={(v) => {
            const trimmed = v.trim();
            let right: ConditionRule["right"] = v;
            if (trimmed === "true") right = true;
            else if (trimmed === "false") right = false;
            else if (trimmed !== "" && !Number.isNaN(Number(trimmed))) right = Number(trimmed);
            onChange({ ...rule, right });
          }}
        />
      ) : null}
    </div>
  );
}
